import { useLazyGetAvailableRateGroups } from "@/actions/hooks/useLazyGetAvailableRateGroups.ts";
import { AccommodationModalDetailed } from "@/components/AccommodationModalDetailed/AccommodationModalDetailed.tsx";
import Container from "@/components/Container/Container.tsx";
import { ChevronRight } from "@/components/Icons/utility/ChevronRight.tsx";
import { Loader } from "@/components/Loader/Loader.tsx";
import Modal from "@/components/Modal/Modal.tsx";
import { ReservationSidebar } from "@/components/ReservationSidebar/ReservationSidebar.tsx";
import { clearReservationOfferURLParam } from "@/helpers/clearReservationOfferURLParam.ts";
import { useGTMAccommodationList } from "@/helpers/gtm/useGTMAccommodationList.ts";
import { URLParam } from "@/helpers/interfaces/URLParam.ts";
import { scrollToTop } from "@/helpers/scrollToTop.ts";
import { RateGroup } from "@/helpers/types/types.ts";
import { useCartDrawerState } from "@/helpers/useCartDrawerState.ts";
import { useGetOrganization } from "@/helpers/useGetOrganization.ts";
import { useModalState } from "@/helpers/useModalState.ts";
import { PickingHeader } from "@/pages/AccommodationPicking/components/PickingHeader/PickingHeader.tsx";
import { RPCancelationPolicyModalDetailed } from "@/pages/AccommodationPicking/components/RPCancelationPolicyModalDetailed/RPCancelationPolicyModalDetailed.tsx";
import { RateGroupsList } from "@/pages/AccommodationPicking/components/RateGroupsList/RateGroupsList.tsx";
import { useCheckCartAccommodationAvailibility } from "@/pages/AccommodationPicking/hooks/useCheckCartAccommodationAvailibility.ts";
import { useClearRateGroupURLParams } from "@/pages/AccommodationPicking/hooks/useRateGroupURLParams.ts";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { AppPage } from "@/stores/EngineStore/enums/AppPage.ts";
import { ModalKey } from "@/stores/ModalStore/ModalKey.ts";
import { useModalStore } from "@/stores/ModalStore/useModalStore.ts";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

export const AccommodationPicking = () => {
  const { t } = useTranslation();
  const { modalState, handleCloseModal, handleOpenModal } = useModalState<RateGroup>();
  const {
    open,
    closeModal: handleCloseRPCancelationPolicyModalState,
    key,
  } = useModalStore(
    useShallow((state) => ({ open: state.open, closeModal: state.closeModal, key: state.key })),
  );
  const { getOrganizationLoading } = useGetOrganization();
  const { handleCloseCartDrawer } = useCartDrawerState();
  const clearRateGroupURLParams = useClearRateGroupURLParams();
  const {
    availableRateGroups,
    setAvailableRateGroups,
    setPage,
    defaultRateGroups,
    accommodationTypeId,
    setAccommodationTypeId,
  } = useEngineStore(
    useShallow((state) => ({
      defaultRateGroups: state.defaultRateGroups,
      availableRateGroups: state.availableRateGroups,
      setAvailableRateGroups: state.setAvailableRateGroups,
      setPage: state.setPage,
      accommodationTypeId: state.accommodationTypeId,
      setAccommodationTypeId: state.setAccommodationTypeId,
    })),
  );
  const { handleInitialFetchAvailableRGs, handleFetchAvailableRGs, getAvailableRGsLoading } =
    useLazyGetAvailableRateGroups();

  const fetchAvailableRgsAndClearURLParams = async () => {
    setAccommodationTypeId(undefined);
    await new Promise<void>((resolve) => {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.delete(URLParam.AccommodationType);
      window.history.replaceState({}, "", `?${urlParams.toString()}`);
      resolve();
    });
    setAvailableRateGroups([]);
    clearReservationOfferURLParam();
    await clearRateGroupURLParams();
    return await handleFetchAvailableRGs();
  };

  useGTMAccommodationList();
  const availabilityDisabled = useCheckCartAccommodationAvailibility();

  useEffect(() => {
    // fetches initial data if URL params are present
    void handleInitialFetchAvailableRGs();
  }, [handleInitialFetchAvailableRGs]);

  const renderViewAllAccommodationsButton = () => {
    if (
      (defaultRateGroups.length <= 0 && !accommodationTypeId) ||
      getAvailableRGsLoading ||
      getOrganizationLoading
    ) {
      return null;
    }

    return (
      <div className={"all-acc-units-container"}>
        <button className="c-be-link" onClick={fetchAvailableRgsAndClearURLParams}>
          <span className="c-be-link__label">{t("viewAllAccommodations")}</span>
          <span className="c-be-link__suffix">
            <ChevronRight />
          </span>
        </button>
      </div>
    );
  };

  return (
    <div>
      <main className="o-page">
        <div className="o-page__inner">
          <PickingHeader handleFetchAvailableRGs={handleFetchAvailableRGs} />
          <section className="accommodations c-accommodations-wrapper">
            <Container>
              {renderViewAllAccommodationsButton()}
              <div className="c-accommodations">
                <div className="c-accommodations__list">
                  <Loader showLabel inline loading={getAvailableRGsLoading || getOrganizationLoading} />
                  <RateGroupsList
                    availableRateGroups={availableRateGroups}
                    isLoading={getAvailableRGsLoading || getOrganizationLoading}
                    handleOpenModal={handleOpenModal}
                  />
                </div>
                <div className="c-accommodations__summary">
                  <div className="c-accommodations__summary-inner">
                    <ReservationSidebar
                      hideRemoveRate
                      hideDatePicker
                      continueBtnPopoverText={
                        availabilityDisabled ? t("availabilityPopoverContent") : undefined
                      }
                      onContinue={() => {
                        setPage(AppPage.ExtraOptions);
                        handleCloseCartDrawer();
                        scrollToTop();
                      }}
                      continueDisabled={availabilityDisabled}
                    />
                  </div>
                </div>
              </div>
            </Container>
          </section>
        </div>
      </main>
      <Modal isOpen={modalState.open} onClose={handleCloseModal}>
        <AccommodationModalDetailed previewRG={modalState.editObject} handleCloseModal={handleCloseModal} />
      </Modal>
      <Modal
        isOpen={open && key === ModalKey.RPCancelationPolicy}
        onClose={handleCloseRPCancelationPolicyModalState}
      >
        <RPCancelationPolicyModalDetailed />
      </Modal>
    </div>
  );
};
